/**
 * S3-MIG-CARD-001 (Jun 2026) — Admin Migration Progress Card.
 * Read-only monitor for the S3 legacy backfill (Phase 0 audit, Phase 1
 * migration, prune). Polls /api/admin/s3-backfill/overview every 15s.
 * Renders nothing until a dry-run report exists.
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { CloudUpload, Database, ShieldCheck, PauseCircle, Activity } from 'lucide-react';
import { apiRequest } from '../../utils/adminApi';

const fmtGB = (mb) => (mb >= 1024 ? `${(mb / 1024).toFixed(1)} GB` : `${(mb || 0).toFixed(0)} MB`);

const PhaseRow = ({ icon: Icon, label, value, sub, tone, testid }) => (
  <div className="flex items-center gap-3 py-2" data-testid={testid}>
    <div className={`p-1.5 rounded-lg ${tone} text-white shadow-sm flex-shrink-0`}>
      <Icon className="w-3.5 h-3.5" />
    </div>
    <div className="min-w-0 flex-1">
      <div className="text-xs font-medium text-slate-500">{label}</div>
      <div className="text-sm font-semibold text-slate-800 truncate">{value}</div>
    </div>
    {sub && <div className="text-xs text-slate-400 text-right flex-shrink-0">{sub}</div>}
  </div>
);

export const MigrationProgressCard = () => {
  const [data, setData] = useState(null);
  const pollRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const res = await apiRequest('/admin/s3-backfill/overview');
      setData(res);
    } catch {
      /* card is optional — stay hidden on error */
    }
  }, []);

  useEffect(() => {
    load();
    pollRef.current = setInterval(load, 15000);
    return () => clearInterval(pollRef.current);
  }, [load]);

  if (!data?.dry_run) return null;

  const dry = data.dry_run;
  const prog = data.run_progress;
  const auth = data.authorization;
  const total = data.total_legacy_files || 0;
  const dryDone = dry.status === 'complete';
  const dryProcessed = dry.summary?.total_legacy_files ?? dry.progress?.processed ?? 0;
  const drySizeMb = dry.summary?.total_size_mb ?? dry.progress?.size_mb_so_far ?? 0;

  const runPhase = prog?.phase || 'not_started';
  const migrated = prog?.migrated ?? 0;
  const runPct = total > 0 ? Math.min(100, ((migrated + (prog?.skipped ?? 0)) / total) * 100) : 0;
  const authActive = auth?.active && new Date(auth.expires_at) > new Date();

  return (
    <div className="ahp-fade-up bg-white/80 backdrop-blur-md border border-slate-200/80 rounded-2xl p-5 shadow-sm hover:shadow-md transition-shadow duration-200 mb-6"
      style={{ animationDelay: '960ms' }} data-testid="migration-progress-card">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <div className="p-2 rounded-xl bg-gradient-to-br from-sky-500 to-indigo-600 text-white shadow-md shadow-sky-500/25">
            <CloudUpload className="w-4 h-4" />
          </div>
          <div>
            <div className="text-sm font-semibold text-slate-800">S3 Storage Migration</div>
            <div className="text-xs text-slate-500">Legacy MongoDB blobs → AWS S3</div>
          </div>
        </div>
        <div className="flex items-center gap-2" data-testid="migration-run-state">
          {runPhase === 'run' && (
            <span className="inline-flex items-center gap-1.5 text-xs font-medium text-sky-700 bg-sky-50 border border-sky-200 rounded-full px-2.5 py-1">
              <Activity className="w-3 h-3 animate-pulse" /> Migrating
            </span>
          )}
          {runPhase === 'run_paused' && (
            <span className="inline-flex items-center gap-1.5 text-xs font-medium text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2.5 py-1">
              <PauseCircle className="w-3 h-3" /> Paused
            </span>
          )}
          {runPhase === 'run_complete' && (
            <span className="inline-flex items-center gap-1.5 text-xs font-medium text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2.5 py-1">
              <ShieldCheck className="w-3 h-3" /> Migration complete
            </span>
          )}
          {authActive && runPhase !== 'run_complete' && (
            <span className="text-xs text-slate-400" data-testid="migration-auth-window">
              auto-resume until {new Date(auth.expires_at).toLocaleString(undefined, { weekday: 'short', hour: 'numeric', minute: '2-digit' })}
            </span>
          )}
        </div>
      </div>

      <div className="divide-y divide-slate-100">
        <PhaseRow icon={Database} tone="bg-gradient-to-br from-slate-500 to-slate-600"
          label="Phase 0 — Audit (read-only)"
          value={dryDone
            ? `Complete — ${dryProcessed.toLocaleString()} files · ${fmtGB(drySizeMb)}`
            : `${(dry.status || 'pending')} — ${(dry.progress?.processed ?? 0).toLocaleString()} files scanned`}
          sub={dryDone ? '100% safe to migrate' : null}
          testid="migration-phase0-row" />
        <div className="py-2" data-testid="migration-phase1-row">
          <div className="flex items-center gap-3">
            <div className="p-1.5 rounded-lg bg-gradient-to-br from-sky-500 to-indigo-600 text-white shadow-sm flex-shrink-0">
              <CloudUpload className="w-3.5 h-3.5" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-xs font-medium text-slate-500">Phase 1 — Migration (blob-preserving)</div>
              <div className="text-sm font-semibold text-slate-800" data-testid="migration-phase1-stats">
                {runPhase === 'not_started'
                  ? 'Scheduled — awaiting authorized window'
                  : `${migrated.toLocaleString()} migrated · ${(prog?.failed ?? 0)} failed · ${(prog?.skipped ?? 0)} skipped`}
              </div>
            </div>
            {total > 0 && runPhase !== 'not_started' && (
              <div className="text-xs font-semibold text-slate-600 tabular-nums flex-shrink-0" data-testid="migration-phase1-pct">
                {runPct.toFixed(1)}%
              </div>
            )}
          </div>
          {total > 0 && runPhase !== 'not_started' && (
            <div className="mt-2 h-1.5 bg-slate-100 rounded-full overflow-hidden">
              <div className="h-full bg-gradient-to-r from-sky-500 to-indigo-500 rounded-full transition-all duration-700"
                style={{ width: `${runPct}%` }} data-testid="migration-progress-bar" />
            </div>
          )}
        </div>
        <PhaseRow icon={ShieldCheck} tone="bg-gradient-to-br from-emerald-500 to-teal-600"
          label="Phase 3 — Prune (gated on approval)"
          value={prog?.pruned ? `${prog.pruned.toLocaleString()} blobs pruned` : 'Not started'}
          testid="migration-phase3-row" />
      </div>
    </div>
  );
};

export default MigrationProgressCard;
